/**
 * Overlay apply/build helpers (M9-B) — the ONE hook that seats an `overlay`
 * block onto a MapSpec, mirroring `applyAnalysisSpec` (M8). The UI (and, later,
 * an NL-emitted overlay intent) land through this so there is one declarative
 * result the M4 render + M6 embed redraw. Pure: returns a new spec.
 */

import type { MapSpec } from "@/lib/map/mapspec";
import { OVERLAY_VERSION } from "./spec";
import type { OverlaySpec, OverlayClassification } from "./spec";
import { DEFAULT_RAMP_FAMILY } from "./ramp";
import type { Country, OverlaySource, Tier } from "./types";

/** Seat (or clear) the `overlay` block on a MapSpec. `undefined` = overlay off. */
export function applyOverlaySpec(mapSpec: MapSpec, overlay: OverlaySpec | undefined): MapSpec {
  return { ...mapSpec, overlay };
}

/** Default overlay opacity when a variable is first applied. */
export const DEFAULT_OVERLAY_OPACITY = 0.7;

export interface BuildOverlayInput {
  source: OverlaySource;
  country: Country;
  variableId: string;
  tier: Tier;
  classification?: OverlayClassification;
  rampTokenId?: string;
  opacity?: number;
  vintage?: string;
}

/** Build a valid, current-version OverlaySpec from a picker selection. */
export function buildOverlaySpec(input: BuildOverlayInput): OverlaySpec {
  return {
    version: OVERLAY_VERSION,
    source: input.source,
    country: input.country,
    variableId: input.variableId,
    tier: input.tier,
    classification: input.classification ?? { method: "quantile", classes: 5 },
    rampTokenId: input.rampTokenId ?? DEFAULT_RAMP_FAMILY,
    opacity: input.opacity ?? DEFAULT_OVERLAY_OPACITY,
    ...(input.vintage ? { vintage: input.vintage } : {}),
  };
}
